import { Stack, Text } from '@chakra-ui/react';
import type { CompanionRecentActivityDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/**
 * Who answered what, lately.
 *
 * The name matters: a colleague opening the panel and finding it empty should
 * see that someone handled the cards, not conclude the companion found nothing.
 * A closure with no person behind it — a business event, a reconciliation pass —
 * says so instead of borrowing a name.
 */

interface CompanionRecentActivityProps {
  entries: CompanionRecentActivityDto[];
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

const relativeTime = (iso: string, language: string): string => {
  const occurred = Date.parse(iso);
  if (!Number.isFinite(occurred)) return '';
  const elapsed = occurred - Date.now();
  const formatter = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(elapsed) >= ms) return formatter.format(Math.round(elapsed / ms), unit);
  }
  return formatter.format(0, 'minute');
};

export const CompanionRecentActivity = ({ entries }: CompanionRecentActivityProps) => {
  const { t, i18n } = useTranslation();
  if (entries.length === 0) return null;
  const language = i18n?.language ?? 'en';

  return (
    <Stack gap="4px" data-testid="companion-recent-activity">
      {entries.map((entry) => (
        <Text key={entry.actionId} fontSize="12px" color="inkSubtle" lineHeight="1.5">
          {entry.actorDisplayName
            ? t('companion.panel.activity.byActor', {
                actor: entry.actorDisplayName,
                kind: t(`companion.kinds.${entry.kind}`),
                when: relativeTime(entry.occurredAt, language),
              })
            : t('companion.panel.activity.automatic', {
                kind: t(`companion.kinds.${entry.kind}`),
                when: relativeTime(entry.occurredAt, language),
              })}
        </Text>
      ))}
    </Stack>
  );
};
